import { clsx } from 'clsx';

import {
  Checkbox as MuiCheckbox,
  checkboxClasses as muiCheckboxClasses,
  css,
  styled,
} from '@mui/material';

import { CheckboxChecked, CheckboxIndeterminate, CheckboxUnchecked } from './icons';
import { checkboxClasses } from './theme';

import type { CheckboxOwnerState, CheckboxProps } from './types';

export function Checkbox(props: CheckboxProps) {
  // useThemeProps 제거 및 defaultProps 인라인 처리 (원본 theme.ts에는 defaultProps 없음)
  const {
    className,
    selectorOverrides,
    slotProps,
    'aria-describedby': ariaDescribedBy,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    ...other
  } = props;
  const { active = '&:active', hover = '&:hover' } = selectorOverrides ?? {};

  const ownerState: CheckboxOwnerState = {
    ...props,
    selectorOverrides: { ...selectorOverrides, active, hover },
  };

  // 동적 클래스만 생성 (고정 클래스는 target으로 처리)
  const getDynamicClasses = () => {
    return {
      root: clsx(),
      // Checkbox는 기본적으로 동적 클래스가 없음 (모든 스타일이 조건부로 적용됨)
    };
  };

  const dynamicClasses = getDynamicClasses();

  // MUI Checkbox의 root는 role이 없는 `span`이라 여기에 aria-label/labelledby/describedby가 붙으면
  // 금지된 속성(aria-prohibited-attr)이 되고, 정작 실제 컨트롤인 내부 `<input type="checkbox">`는
  // 접근 가능한 이름이 없는 상태로 남는다. 접근성 속성은 root가 아니라 input 슬롯으로 전달한다.
  const ariaProps = {
    'aria-describedby': ariaDescribedBy,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
  };
  const externalInputSlotProps = slotProps?.input;
  const mergedSlotProps: CheckboxProps['slotProps'] = {
    ...slotProps,
    // 호출부가 직접 넘긴 input slotProps가 우선하도록 뒤에 병합한다.
    input:
      typeof externalInputSlotProps === 'function'
        ? inputOwnerState => ({ ...ariaProps, ...externalInputSlotProps(inputOwnerState) })
        : { ...ariaProps, ...externalInputSlotProps },
  };

  return (
    <CheckboxRoot
      checkedIcon={<CheckboxChecked />}
      className={clsx(dynamicClasses.root, className)}
      disableRipple
      icon={<CheckboxUnchecked />}
      indeterminateIcon={<CheckboxIndeterminate />}
      ownerState={ownerState}
      slotProps={mergedSlotProps}
      {...other}
    />
  );
}

// 복잡한 color 스타일 헬퍼 함수 (원본 theme.ts에서 이전)
const checkboxColor =
  (interactive: string, strongest: string, danger: string) =>
  ({ error }: { error?: boolean }) => css`
    color: ${error ? danger : interactive};
    &.${muiCheckboxClasses.indeterminate}, &.${muiCheckboxClasses.checked} {
      color: ${error ? danger : strongest};
    }
  `;

const CheckboxRoot = styled(MuiCheckbox, {
  target: checkboxClasses.root,
})<{ ownerState: CheckboxOwnerState }>`
  /* 원본 theme.ts의 root 스타일 */
  font-size: 16px;
  padding: 0;

  ${({ ownerState, theme }) => {
    return css`
      /* 기본 color 스타일 */
      ${checkboxColor(
        theme.color['interactive/default'],
        theme.color['interactive/stronger/default'],
        theme.color['interactive/danger/default'],
      )(ownerState)}

      /* disabled가 아닐 때만 인터랙션 스타일 적용 */
      &:not(&.${muiCheckboxClasses.disabled}) {
        ${ownerState.selectorOverrides?.hover ?? '&:hover'} {
          ${checkboxColor(
            theme.color['interactive/hover'],
            theme.color['interactive/stronger/hover'],
            theme.color['interactive/danger/hover'],
          )(ownerState)}
        }

        ${ownerState.selectorOverrides?.active ?? '&:active'} {
          ${checkboxColor(
            theme.color['interactive/press'],
            theme.color['interactive/stronger/press'],
            theme.color['interactive/danger/press'],
          )(ownerState)}
        }

        ${theme.mixins.focusRing('outside', {
          selectors: { focus: `&.Mui-focusVisible` },
        })};
      }

      /* disabled 상태 스타일 */
      &.${muiCheckboxClasses.disabled} {
        ${checkboxColor(
          theme.color['interactive/disabled'],
          theme.color['interactive/strongest/disabled'],
          theme.color['interactive/danger/disabled'],
        )(ownerState)}
      }

      /* SVG 아이콘 스타일 */
      & > svg {
        width: 1em;
        height: 1em;
        vertical-align: -0.125em;
      }
    `;
  }}
`;
